import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { initFirebase } from './firebase';
import { getPlatformConfig, getDefaultPlatformConfig } from './services/config';
import { PlatformConfig } from './types';
import { useGeolocation } from './hooks/useGeolocation';

// ⭐ Lazy-loaded routes - chunks split per page for faster first paint
const HomePage = lazy(() => import('./pages/HomePage'));
const PostPage = lazy(() => import('./pages/PostPage'));
const MapPage = lazy(() => import('./pages/MapPage'));
const RiTownPage = lazy(() => import('./pages/RiTownPage'));
const InboxPage = lazy(() => import('./pages/InboxPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AuthPage = lazy(() => import('./pages/AuthPage'));
const TosPage = lazy(() => import('./pages/TosPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const EarningsPage = lazy(() => import('./pages/EarningsPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const PayoutsPage = lazy(async () => {
  const mod = await import('./pages/PaymentsPage');
  return { default: mod.PayoutsPage };
});
const TrustSafetyPage = lazy(() => import('./pages/TrustSafetyPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const ProhibitedQuestsPage = lazy(() => import('./pages/ProhibitedQuestsPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const QuestsPage = lazy(() => import('./pages/QuestsPage'));
const MeetQPage = lazy(() => import('./pages/MeetQPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const QuestDetailsPage = lazy(() => import('./pages/QuestDetailsPage'));
const MyQuestsPage = lazy(() => import('./pages/MyQuestsPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const QuestDashboardPage = lazy(() => import('./pages/QuestDashboardPage'));
const CommunityBoard = lazy(() => import('./components/CommunityBoard'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const PaymentCanceledPage = lazy(() => import('./pages/PaymentCanceledPage'));
const BusinessesPage = lazy(() => import('./pages/BusinessesPage'));
const BusinessProfilePage = lazy(() => import('./pages/BusinessProfilePage'));
// The ChatGPT plugin launch page. Public, static-friendly, QR-friendly.
const GptPage = lazy(() => import('./pages/GptPage'));

import Toast from './components/Toast';
import BottomNav from './components/BottomNav';
import TopBar from './components/TopBar';
import OnboardingEnforcement from './components/OnboardingEnforcement';
import Footer from './components/Footer';
import { PageSkeleton } from './components/Skeleton';
import ErrorBoundary from './components/ErrorBoundary';
import NotificationCenter from './components/NotificationCenter';
import InstallPrompt from './components/InstallPrompt';
import { useSeoHead } from './seo/useSeoHead';

// ⭐ Performance: routes whose content never depends on the signed-in user.
// These render immediately instead of waiting for the Firebase Auth
// round-trip, which used to put a multi-second, network-bound barrier in
// front of first paint / LCP on every public landing page. Auth-dependent
// routes (profile, settings, inbox, admin, onboarding, ...) keep the
// existing boot splash so their first render is still correct.
const PUBLIC_ROUTES = new Set([
  'home', '', 'map', 'about', 'quests', 'meetQ', 'faq', 'support',
  'trust-and-safety', 'prohibited-quests', 'terms', 'privacy', 'tos',
  'how-it-works',
  // The ChatGPT launch page must render for a signed-out visitor arriving
  // from a QR code, a Facebook post, or a pinned comment.
  'gpt',
  // Featured Local Businesses: editorial pages that must render for a
  // signed-out visitor arriving from a business's own shared link.
  'businesses',
]);

// ⭐ Routes whose page component declares a `config` prop. Only these may fetch
// the server-backed platform config - and therefore only these download the
// firebase/functions SDK. Deliberately an allow-list rather than "everything
// that is not public", so an unknown path rendering NotFoundPage never pays
// for a callable it cannot use.
const ROUTES_WITH_PLATFORM_CONFIG = new Set(['post', 'settings', 'profile']);

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, appUser, loading, role } = useAuth();
  const [bootDone, setBootDone] = useState(false);
  const [config, setConfig] = useState<PlatformConfig | null>(() =>
    getDefaultPlatformConfig()
  );
  const [showAuth, setShowAuth] = useState(false);
  const { position, startWatching } = useGeolocation();

  // Keep document.head in sync with the active route. Resets metadata on
  // unknown routes so a 404 never inherits homepage defaults.
  useSeoHead();

  const currentPage = location.pathname === '/' ? 'home' : location.pathname.slice(1);

  useEffect(() => {
    initFirebase();
  }, []);

  // ⭐ Platform config (Stripe/Connect/Identity availability, FCM readiness,
  // boost cost, payout minimums) is served by the `getConfigStatus` callable,
  // and fetching it downloads the firebase/functions SDK.
  //
  // No public marketing or SEO route renders from it, so those routes must not
  // pay that cost. The synchronous fallback seed above already hands every
  // consumer the exact object it received before on a config-fetch failure, so
  // this effect only refreshes from the server on the few routes whose page
  // component actually declares a `config` prop here in AppContent - and it
  // waits for an idle frame, so it can never sit on the LCP path.
  //
  // Allow-list (not a deny-list) on purpose: an unknown path renders
  // NotFoundPage, which must not pull the Functions SDK either.
  useEffect(() => {
    if (!ROUTES_WITH_PLATFORM_CONFIG.has(currentPage)) return;

    let cancelled = false;
    const load = () => {
      getPlatformConfig().then((cfg) => {
        if (!cancelled) setConfig(cfg);
      });
    };

    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    if (typeof win.requestIdleCallback === 'function') {
      const idleId = win.requestIdleCallback(load, { timeout: 3000 });
      return () => {
        cancelled = true;
        win.cancelIdleCallback?.(idleId);
      };
    }

    const timer = setTimeout(load, 1200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [currentPage]);

  // ⭐ TRACK F (Best Practices): geolocation is NEVER requested on page load.
  // `startWatching()` is only invoked when the user explicitly taps
  // "Find quests near me" / a radius chip / the map. The browser permission
  // prompt therefore always follows a user gesture.
  const handleEnableLocation = useCallback(() => {
    startWatching();
  }, [startWatching]);

  useEffect(() => {
    if (!loading && user && appUser && !appUser.onboarded && currentPage !== 'onboarding') {
      navigate('/onboarding');
    }
  }, [loading, user, appUser, navigate, currentPage]);

  const handleNavigate = useCallback((p: string) => {
    if (p === 'auth') { setShowAuth(true); return; }
    if (p === 'home') { navigate('/'); return; }
    navigate(`/${p}`);
  }, [navigate]);

  // Never show a blank shell while auth state is still resolving - but never
  // block a public route on it either. Those pages render their real content
  // with user=null and simply re-render once auth resolves; the onboarding
  // redirect below is already an effect that waits for `loading`.
  const isPublicRoute =
    PUBLIC_ROUTES.has(currentPage) ||
    currentPage.startsWith('ri/') ||
    currentPage.startsWith('quest/') ||
    currentPage.startsWith('businesses/');
  const booting = loading && !bootDone && !isPublicRoute;

  useEffect(() => {
    if (!loading) setBootDone(true);
  }, [loading]);

  const hideNav = ['terms', 'privacy', 'how-it-works', 'reports', 'tos', 'onboarding', 'dashboard', 'meetQ'].includes(currentPage);

  if (booting) {
    return (
      <div className="app-shell">
        <main className="main-content">
          <div className="loading-screen" style={{ minHeight: '100dvh' }}>
            <div className="loading-spinner" />
            <span>Loading SideQuests…</span>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <TopBar page={currentPage} user={user} appUser={appUser} onNavigate={handleNavigate} onAuthClick={() => setShowAuth(true)} config={config} />
      {/* ⭐ Track E: main landmark is the skip-link target (#main). */}
      <main id="main" className="main-content" style={hideNav ? { paddingBottom: 0 } : undefined}>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route path="/" element={<HomePage user={user} appUser={appUser} config={config} position={position} onNavigate={handleNavigate} onEnableLocation={handleEnableLocation} />} />
          <Route path="/post" element={user ? <PostPage user={user} appUser={appUser} config={config} onNavigate={handleNavigate} /> : <AuthPage onClose={() => navigate('/')} />} />
          <Route path="/post/payment-success" element={user ? <PaymentSuccessPage /> : <AuthPage onClose={() => navigate('/')} />} />
          <Route path="/post/payment-canceled" element={user ? <PaymentCanceledPage /> : <AuthPage onClose={() => navigate('/')} />} />
          <Route path="/map" element={<MapPage user={user} config={config} position={position} onNavigate={handleNavigate} />} />
          <Route path="/ri/:slug" element={<RiTownPage user={user} appUser={appUser} config={config} position={position} onNavigate={handleNavigate} />} />
          <Route path="/inbox" element={<InboxPage user={user} />} />
          {/* ⭐ /quests is the canonical marketplace route. /feed is LEGACY ONLY
              and permanently redirects, so old links and bookmarks still work. */}
          <Route path="/quests" element={<QuestsPage user={user} appUser={appUser} config={config} position={position} onNavigate={handleNavigate} onEnableLocation={handleEnableLocation} />} />
          <Route path="/feed" element={<Navigate to="/quests" replace />} />
          <Route path="/settings" element={<ProfilePage user={user} appUser={appUser} config={config} />} />
          <Route path="/quest/:id" element={<QuestDetailsPage jobId={location.pathname.split('/')[2] || ''} onNavigate={handleNavigate} />} />
          <Route path="/profile" element={<ProfilePage user={user} appUser={appUser} config={config} />} />
          <Route path="/my-quests" element={<MyQuestsPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/dashboard" element={<QuestDashboardPage />} />
          <Route path="/community" element={<CommunityBoard />} />
          <Route path="/notifications" element={<NotificationCenter onNavigate={handleNavigate} />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/tos" element={<TosPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/earnings" element={<EarningsPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/payouts" element={<PayoutsPage />} />
          <Route path="/trust-and-safety" element={<TrustSafetyPage />} />
          <Route path="/prohibited-quests" element={<ProhibitedQuestsPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/support" element={<SupportPage />} />
          {/* Meet Q. Canonical casing is /meetQ; the lowercase and hyphenated
              spellings redirect so a typed or shared link always resolves. */}
          <Route path="/meetQ" element={<MeetQPage />} />
          <Route path="/meet-q" element={<Navigate to="/meetQ" replace />} />
          <Route path="/meetq" element={<Navigate to="/meetQ" replace />} />
          {/* Early Access was removed from the product. */}
          <Route path="/early-access" element={<Navigate to="/" replace />} />
          <Route path="/about" element={<AboutPage />} />
          {/* Featured Local Businesses: the editorial listing plus one
              shareable profile per published business. Both read the sanitized
              publicBusinesses projection and render for signed-out visitors. */}
          <Route path="/businesses" element={<BusinessesPage />} />
          <Route path="/businesses/:slug" element={<BusinessProfilePage />} />
          {/* ChatGPT plugin launch page. The canonical spelling is /gpt; the
              alternative spellings redirect so a typed or shared link resolves. */}
          <Route path="/gpt" element={<GptPage />} />
          <Route path="/chatgpt" element={<Navigate to="/gpt" replace />} />
          <Route path="/contact" element={<Navigate to="/support" replace />} />
          <Route path="/ri" element={<Navigate to="/map" replace />} />
          <Route path="/admin" element={<AdminDashboardPage />} />
          <Route path="/auth" element={<AuthPage onClose={() => navigate('/')} />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      <OnboardingEnforcement geoState="granted" geoFrozen={false} />
      {currentPage !== 'map' && <Footer />}
      {!hideNav && <BottomNav page={currentPage} onNavigate={handleNavigate} />}
      <InstallPrompt />
      {showAuth && (
        <Suspense fallback={null}>
          <AuthPage onClose={() => setShowAuth(false)} />
        </Suspense>
      )}
      <Toast />
    </div>
  );
}

function App() {
  useEffect(() => { initFirebase(); }, []);
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
